import {CardVariantType, FormSubmitHandler} from 'platform/components';

import {
  CreateCommentApiArg,
  DeleteCommentApiArg,
  UpdateCommentApiArg,
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useGetCommentsQuery,
  useUpdateCommentMutation,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';

import {TestIdProps} from 'shared/web';

import {useGetUserOptions} from '../../hooks/useGetUserOptions';
import {handleApiError} from '../../utils/handleApiError';
import {CommentsCard} from '../CommentsCard/CommentsCard';
import {CommentCardForm} from '../CommentsCard/components/CommentsCardInput';

interface CommentsWidgetProps extends TestIdProps {
  resourceId: EntityResourceIds;
  recordId: string;
  title?: string;
  cardVariant?: CardVariantType;
  isAddDisabled?: boolean;
  isDeleteDisabled?: boolean;
  isEditDisabled?: boolean;
}

export function CommentsWidget(props: CommentsWidgetProps) {
  const {
    data: comments,
    isLoading: isFetchingCommentsLoading,
    isError: isFetchingCommentsErrored,
  } = useGetCommentsQuery({
    resourceId: props.resourceId,
    recordId: props.recordId,
  });
  const {userOptions} = useGetUserOptions();

  const [deleteComment, {isLoading: isDeleteCommentLoading}] = useDeleteCommentMutation();
  const [updateComment, {isLoading: isEditCommentLoading}] = useUpdateCommentMutation();
  const [createComment, {isLoading: isSendCommentLoading}] = useCreateCommentMutation();

  const onCommentSubmit: FormSubmitHandler<CommentCardForm> = async (values, _, reset) => {
    const arg: CreateCommentApiArg = {
      resourceId: props.resourceId,
      recordId: props.recordId,
      createCommentRequestBody: {
        text: values.comment,
      },
    };
    await createComment(arg)
      .unwrap()
      .then(() => {
        reset();
      })
      .catch(handleApiError);
  };

  const onCommentDelete = async (commentId: string) => {
    const arg: DeleteCommentApiArg = {
      resourceId: props.resourceId,
      recordId: props.recordId,
      commentId,
    };
    await deleteComment(arg).unwrap().catch(handleApiError);
  };

  const onCommentEdit = async (commentId: string, text: string) => {
    const arg: UpdateCommentApiArg = {
      resourceId: props.resourceId,
      recordId: props.recordId,
      commentId,
      updateCommentRequestBody: {text},
    };
    await updateComment(arg).unwrap().catch(handleApiError);
  };

  return (
    <CommentsCard
      cardVariant={props.cardVariant}
      title={props.title}
      comments={comments}
      isSendCommentLoading={isSendCommentLoading}
      isFetchingCommentsLoading={isFetchingCommentsLoading}
      isFetchingCommentsErrored={isFetchingCommentsErrored}
      isDeleteCommentLoading={isDeleteCommentLoading}
      isEditCommentLoading={isEditCommentLoading}
      onSubmit={onCommentSubmit}
      onDelete={onCommentDelete}
      onEdit={onCommentEdit}
      userOptions={userOptions}
      isAddDisabled={props.isAddDisabled}
      isDeleteDisabled={props.isDeleteDisabled}
      isEditDisabled={props.isEditDisabled}
      data-testid={props['data-testid']}
    />
  );
}
